import { Strophe } from 'react-strophe'
import * as xmpp from '../../global/xmppPayloadHelper'
import { extractError, send, sendIQ, validateConnected } from './serverConnection'
import {
  parseArchivedMessageReceived,
  parseGroupChatMembers,
  parsePaginationDetails,
  parseGroupChatMembersCount,
} from '../../global/messageParserHelper'
import { Chat } from '../../global/chatSettingsConfig'
import chatLog from '../../global/chat-log'
import { isValidJid } from '../../global/build-jid'
import orchestrationApi from '../orchestration'

/*
  Update the private chat room to be private, etc.
*/
export const updateSettingsForPrivateChatRoom = ({ connection, chatRoom, friendlyName }) => {
  chatLog('[Groups] Updating settings for private chat room')
  const stanza = xmpp.payloadToUpdatePrivateChatRoomConfiguration({ chatRoom, friendlyName })
  const parseFn = (response) => response
  return sendIQ({ connection, stanza, parseFn })
}

/*
  Send a message to a group.
  https://xmpp.org/extensions/xep-0045.html
*/
export const postGroupMessage = ({ chatRoom, fromJid, fromResource, message, fromDisplayName, connection }) => {
  chatLog('[Groups] Sending group message')
  const stanza = xmpp.payloadToSendMessageToChatRoom({ chatRoom, fromJid, fromResource, message, fromDisplayName })
  return send({ connection, stanza })
}

export const createGroupChat = async ({ chatRoom, fromJid, friendlyName, connection }) => {
  if (!chatRoom || chatRoom.indexOf(Chat.Settings.MUC_DOMAIN) < 0) {
    throw new Error('[Groups] Invalid chat room provided. Must have proper domain.')
  }
  chatLog(`[Groups] Creating a new group chat room: ${friendlyName} (${chatRoom})`)
  const stanza = xmpp.payloadToCreateOrJoinChatRoom({ chatRoom, fromJid })

  return send({ connection, stanza })
}

export const joinGroupChat = ({ chatRoom, fromJid, connection }) => {
  if (!isValidJid(chatRoom)) {
    console.error(`Found an invalid jid: ${chatRoom}`)
    return Promise.resolve()
  }
  chatLog(`[Groups] Joining a group chat room: ${chatRoom}`)
  return new Promise((resolve, reject) => {
    const successFn = () => resolve()
    const errorFn = (e) => reject(extractError(e))

    const stanza = xmpp.payloadToCreateOrJoinChatRoom({ chatRoom, fromJid })
    validateConnected({ connection, stanza })
    connection.sendPresence(stanza, successFn, errorFn, Chat.Settings.TIMEOUT)
  })
}

/*
  NOTE: This will also give you affiliation and role.
  https://xmpp.org/extensions/xep-0045.html#example-131
 */
export const getPeopleInChatRoom = ({ connection, fromJid, chatRoom }) => {
  if (!isValidJid(chatRoom)) {
    console.error(`Found an invalid jid: ${chatRoom}`)
    return Promise.resolve()
  }
  chatLog('[Groups] Getting list of people in chat room')
  const stanza = xmpp.payloadToGetMembersInChatRoom({ fromJid, chatRoom })
  return sendIQ({ connection, stanza, parseFn: parseGroupChatMembers })
}

export const getPeopleCountInChatRoom = ({ connection, fromJid, chatRoom }) => {
  if (!isValidJid(chatRoom)) {
    console.error(`Found an invalid jid: ${chatRoom}`)
    return Promise.resolve()
  }
  chatLog('[Groups] Getting count of people in chat room')
  const stanza = xmpp.payloadToGetMembersInChatRoom({ fromJid, chatRoom })
  return sendIQ({ connection, stanza, parseFn: parseGroupChatMembersCount })
}

export const getGroupChatHistory = ({ connection, chatRoom, paginationToken, count }) => {
  if (!isValidJid(chatRoom)) {
    console.error(`Found an invalid jid: ${chatRoom}`)
    return Promise.resolve()
  }

  const start = new Date()

  chatLog('[Groups] Getting the group chat history')
  const messages = []
  const messageHandler = connection.addHandler((stanza) => {
    const data = parseArchivedMessageReceived({ stanza, isGroup: true })
    if (data) {
      messages.push(data)
    }

    return true
  }, Chat.NS.MAM_2)

  return new Promise((resolve, reject) => {
    const errorFn = (err) => {
      console.error(err)
      connection.deleteHandler(messageHandler)
      reject(extractError(err))
    }

    const successFn = (stanza1) => {
      const end = new Date()
      chatLog('[Groups] Request finished.')
      connection.deleteHandler(messageHandler)

      const paginate = parsePaginationDetails(stanza1)
      const data = {
        paginationToken: paginate.isComplete ? undefined : paginate.first,
        messages: [...messages],
      }

      const duration = end.getTime() - start.getTime()
      // fire and forget
      try {
        const jabberResource = Strophe.getResourceFromJid(connection.jid)
        orchestrationApi.chatmetrics.logChatHistoryTiming({ jabberResource, jid: chatRoom, duration })
      } catch (e) {
        // ignoring
      }

      resolve(data)
    }

    const stanza = xmpp.payloadToGetChatRoomHistory({ chatRoom, paginationToken, count })
    validateConnected({ connection, stanza })
    connection.sendIQ(stanza, successFn, errorFn, Chat.Settings.TIMEOUT)
  })
}

/*
  Notify the group that user is available to receive messages.
  https://xmpp.org/extensions/xep-0045.html
*/
export const sendGroupChatPresence = ({ connection, fromJid, chatRoom }) => {
  if (!isValidJid(chatRoom)) {
    console.error(`Found an invalid jid: ${chatRoom}`)
    return Promise.resolve()
  }
  chatLog(`[Groups] Updating my status for chat room: ${chatRoom}`)
  const stanza = xmpp.payloadToCreateOrJoinChatRoom({ fromJid, chatRoom })
  return sendIQ({ connection, stanza })
}

/*
  Notify the group the user is available, but wait for the response
  from the server.
  NOTE: To be used prior to listening for unread messages!
*/
export const sendAutoJoinPresence = ({ connection, fromJid, chatRoom }) => {
  if (!isValidJid(chatRoom)) {
    console.error(`Found an invalid jid: ${chatRoom}`)
    return Promise.resolve()
  }
  chatLog(`Updating my status for chat room: ${chatRoom}`)
  const stanza = xmpp.payloadToCreateOrJoinChatRoom({ fromJid, chatRoom })

  return new Promise((resolve) => {
    const resolveFn = () => {
      resolve()

      // Don't keep the listener alive
      return false
    }

    // Add handler for this specific chatRoom
    connection.addHandler(resolveFn, null, 'presence', null, null, chatRoom)
    sendIQ({ connection, stanza })
  })
}

/*
  Notify the group that the user is unavailable.
  https://xmpp.org/extensions/xep-0045.html
*/
export const leaveGroupChatRoom = ({ connection, fromJid, chatRoom }) => {
  if (!isValidJid(chatRoom)) {
    console.error(`Found an invalid jid: ${chatRoom}`)
    return Promise.resolve()
  }
  chatLog('[Groups] Leaving the group chat room')
  const stanza = xmpp.payloadToLeaveChatRoom({ fromJid, chatRoom })
  return sendIQ({ connection, stanza })
}

/*
  This will create a private chat room by declaring presence to a brand new room.
  The chatroom must be unique, such as <guid>@conference.jabber.rootstechchat.org
*/
export const createPrivateChatRoom = ({ connection, chatRoom, fromJid }) => {
  if (!isValidJid(chatRoom)) {
    console.error(`Found an invalid jid: ${chatRoom}`)
    return Promise.resolve()
  }
  chatLog('[Groups] Creating a private chat room')
  const stanza = xmpp.payloadToCreatePrivateChatRoom({ chatRoom, fromJid })
  const parseFn = (response) => response
  return sendIQ({ connection, stanza, parseFn })
}

/*
  Send a message to a person to be invited to the private chat room.
*/
export const sendInvitationToPrivateChatRoomPerson = ({ connection, toJid, chatRoom }) => {
  if (!isValidJid(chatRoom)) {
    console.error(`Found an invalid jid: ${chatRoom}`)
    return Promise.resolve()
  }
  chatLog(`[Groups] Sending invite to ${toJid} to join private chat room`)
  const stanza = xmpp.payloadToSendMessageInvitationToPrivateChatRoom({ toJid, chatRoom })
  const parseFn = (response) => response
  return sendIQ({ connection, stanza, parseFn })
}

export const sendInvitationToPrivateChatRoomPeople = ({ connection, toJidList, chatRoom }) => {
  if (!isValidJid(chatRoom)) {
    console.error(`Found an invalid jid: ${chatRoom}`)
    return Promise.resolve()
  }
  const allCalls = []
  toJidList.forEach((jid) => {
    const validJid = jid.indexOf(`@${Chat.Settings.DOMAIN}`) < 0 ? `${jid}@${Chat.Settings.DOMAIN}` : jid
    allCalls.push(sendInvitationToPrivateChatRoomPerson({ connection, toJid: validJid, chatRoom }))
  })

  return Promise.all(allCalls)
}
